/**
 * Background sync (02-architecture.md Offline; 06-flows.md F10 step 6): when the cloud is
 * reachable and commands are queued, upload them in order with `POST {o}/sync` and handle
 * each command's own result:
 *
 *   applied / replayed   done: leave the queue, the server's copy of the order replaces the
 *                        till's projection once nothing else is waiting on it;
 *   rejected / conflict  the server refused it (see ConflictsDialog): kept, visibly, with the
 *                        server's reason; the order's later commands are held with it;
 *   skipped              a command before it on that order was refused or must be retried;
 *   retry                stays queued, tried again after a pause.
 *
 * `pricing.discrepancy` (the server priced an order the till billed differently and kept the
 * till's bill) arrives with the bill's result and is shown as a notice, not an error.
 */
import { isApiError, isNetworkError, unwrap, type OrderView } from '@qrkey/api-client';
import { api, tokens } from '../app/api.js';
import { queryClient } from '../app/query.js';
import { terminalStore } from '../app/terminal.js';
import { keys } from './cache.js';
import { connectivity } from './connectivity.js';
import { db, kvGet, kvSet, type QueuedCommand } from './db.js';
import { confirm, recount, waiting } from './queue.js';
import { isOfflineSession, upgradeOfflineSession } from './session.js';
import { topUp } from './numbers.js';
import { clearLocalKds } from './kds.js';

const BATCH = 50;
const MAX_ATTEMPTS = 8;

export interface SyncNotice {
  id: string;
  at: number;
  kind: 'discrepancy';
  orderId: string;
  orderNo: number | null;
  keptTotalPaise: number;
  serverTotalPaise: number;
  differencePaise: number;
}

export type SyncState = 'idle' | 'syncing' | 'needs-sign-in' | 'blocked';

type SyncResult = {
  idempotencyKey: string;
  kind: string;
  status: 'applied' | 'replayed' | 'rejected' | 'conflict' | 'skipped' | 'retry';
  code: string | null;
  detail: string | null;
  orderId: string | null;
  version: number | null;
  result: Record<string, unknown> | null;
  current: Record<string, unknown> | null;
  discrepancy: {
    orderId: string;
    keptTotalPaise: number;
    serverTotalPaise: number;
    differencePaise: number;
  } | null;
};

let state: SyncState = 'idle';
const listeners = new Set<() => void>();
function setState(next: SyncState) {
  if (state === next) return;
  state = next;
  listeners.forEach((l) => l());
}
export const syncStatus = {
  get: (): SyncState => state,
  subscribe(l: () => void) {
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  },
};

let running: Promise<void> | null = null;
let again = false;

/** Run a sync now (or after the one in progress). Resolves when the queue is as drained as it can be. */
export function syncNow(): Promise<void> {
  if (running) {
    again = true;
    return running;
  }
  running = (async () => {
    try {
      do {
        again = false;
        await drain();
      } while (again);
    } finally {
      running = null;
      if (state === 'syncing') setState('idle');
    }
  })();
  return running;
}

let soon: ReturnType<typeof setTimeout> | undefined;
/** Sync shortly (coalescing bursts of enqueues). */
export function syncSoon(delayMs = 150): void {
  if (soon) return;
  soon = setTimeout(() => {
    soon = undefined;
    void syncNow();
  }, delayMs);
}

/** Drop a scheduled sync and wait for the one in progress (tests arrange mocks after this). */
export async function settleSync(): Promise<void> {
  if (soon) {
    clearTimeout(soon);
    soon = undefined;
  }
  await running;
}

async function drain(): Promise<void> {
  for (let guard = 0; guard < 20; guard++) {
    if (!connectivity.isOnline()) return;
    const session = tokens.get();
    if (!session) return;
    if (isOfflineSession(session) && !(await upgradeOfflineSession())) {
      setState('needs-sign-in');
      return;
    }
    const now = Date.now();
    const ready = (await waiting()).filter((c) => c.notBefore <= now);
    const first = ready[0];
    if (!first) {
      setState('idle');
      await refreshNumbers();
      // Everything reached the cloud: its KOTs and tickets replace this device's own.
      const t = terminalStore.get();
      if (t) await clearLocalKds(t.outletId);
      return;
    }
    // One outlet and terminal per call.
    const batch = ready
      .filter((c) => c.outletId === first.outletId && c.terminalId === first.terminalId)
      .slice(0, BATCH);
    setState('syncing');
    const proceed = await sendBatch(batch);
    if (!proceed) return;
  }
}

async function refreshNumbers(): Promise<void> {
  const t = terminalStore.get();
  if (!t || !tokens.get() || isOfflineSession(tokens.get())) return;
  await topUp(t.outletId, t.terminalId);
}

/** False when the batch could not be sent at all (stop and wait). */
async function sendBatch(batch: QueuedCommand[]): Promise<boolean> {
  const first = batch[0];
  if (!first) return false;
  let results: SyncResult[];
  try {
    const res = await unwrap(
      api.client.POST('/v1/outlets/{outletId}/sync', {
        params: { path: { outletId: first.outletId } },
        body: {
          terminalId: first.terminalId,
          commands: batch.map((c) => ({
            idempotencyKey: c.idempotencyKey,
            kind: c.kind,
            payload: c.payload,
            clientAt: c.clientAt,
            ...(c.baseVersion !== null ? { baseVersion: c.baseVersion } : {}),
          })),
        },
      }),
    );
    results = res.results as SyncResult[];
    connectivity.reportAnswer();
  } catch (error) {
    if (isNetworkError(error)) {
      connectivity.reportFailure();
      return false;
    }
    if (isApiError(error) && (error.status === 401 || error.status === 403)) {
      // Signed out, or this terminal may not sync: the queue stays for a person to sort out.
      setState(error.code === 'TERMINAL_BLOCKED' ? 'blocked' : 'needs-sign-in');
      return false;
    }
    // A 5xx or a bug: back off, keep everything queued.
    await backoff(batch);
    return false;
  }
  const byKey = new Map(results.map((r) => [r.idempotencyKey, r] as const));
  for (const row of batch) {
    const r = byKey.get(row.idempotencyKey);
    if (!r) {
      await backoff([row]);
      continue;
    }
    await handle(row, r);
  }
  const outlets = new Set(batch.map((b) => b.outletId));
  for (const o of outlets) void queryClient.invalidateQueries({ queryKey: ['pos', o] });
  return true;
}

async function backoff(rows: QueuedCommand[]): Promise<void> {
  for (const row of rows) {
    if (row.seq === undefined) continue;
    const attempts = row.attempts + 1;
    if (attempts >= MAX_ATTEMPTS) {
      await db().queue.update(row.seq, {
        status: 'rejected',
        attempts,
        code: 'GAVE_UP',
        detail: 'The server could not apply this command after several tries',
      });
    } else {
      await db().queue.update(row.seq, {
        attempts,
        notBefore: Date.now() + Math.min(60_000, 2_000 * 2 ** attempts),
      });
    }
  }
}

function orderOf(result: Record<string, unknown> | null): OrderView | null {
  if (!result) return null;
  const candidate = (result['order'] ?? result) as Partial<OrderView>;
  return typeof candidate.id === 'string' && Array.isArray(candidate.lines)
    ? (candidate as OrderView)
    : null;
}

async function handle(row: QueuedCommand, r: SyncResult): Promise<void> {
  const seq = row.seq;
  if (seq === undefined) return;
  switch (r.status) {
    case 'applied':
    case 'replayed': {
      await confirm(seq);
      const order = orderOf(r.result);
      if (order && row.orderId) {
        await db()
          .orders.where('id')
          .equals(row.orderId)
          .modify((o) => {
            o.server = order;
            // Nothing else waiting on it: the server's copy is the order now.
            if (o.pending === 0) o.view = order;
          });
        await kvSet(keys.order(row.outletId, order.id), order);
      }
      if (r.discrepancy) await addNotice(row, r);
      return;
    }
    case 'retry':
      await backoff([row]);
      return;
    case 'skipped':
      if (r.code === 'DEPENDS_ON_RETRY') {
        await db().queue.update(seq, { notBefore: Date.now() + 2_000 });
        return;
      }
      await mark(row, 'conflict', r);
      return;
    case 'conflict':
    case 'rejected': {
      await mark(row, r.status, r);
      if (r.current && row.orderId) {
        const current = orderOf(r.current);
        if (current) {
          await db()
            .orders.where('id')
            .equals(row.orderId)
            .modify((o) => {
              o.server = current;
            });
          await kvSet(keys.order(row.outletId, current.id), current);
        }
      }
      return;
    }
  }
}

async function mark(
  row: QueuedCommand,
  status: 'conflict' | 'rejected',
  r: SyncResult,
): Promise<void> {
  if (row.seq === undefined) return;
  await db().queue.update(row.seq, { status, code: r.code, detail: r.detail });
  if (row.orderId) await recount(row.orderId);
}

async function addNotice(row: QueuedCommand, r: SyncResult): Promise<void> {
  const d = r.discrepancy;
  if (!d) return;
  const k = `notices:${row.outletId}`;
  const held = (await kvGet<SyncNotice[]>(k)) ?? [];
  const order = orderOf(r.result);
  await kvSet(
    k,
    [
      {
        id: row.idempotencyKey,
        at: Date.now(),
        kind: 'discrepancy' as const,
        orderId: d.orderId,
        orderNo: order?.orderNo ?? null,
        keptTotalPaise: Number(d.keptTotalPaise),
        serverTotalPaise: Number(d.serverTotalPaise),
        differencePaise: Number(d.differencePaise),
      },
      ...held,
    ].slice(0, 50),
  );
}

export async function notices(outletId: string): Promise<SyncNotice[]> {
  return (await kvGet<SyncNotice[]>(`notices:${outletId}`)) ?? [];
}

export async function dismissNotice(outletId: string, id: string): Promise<void> {
  const held = await notices(outletId);
  await kvSet(
    `notices:${outletId}`,
    held.filter((n) => n.id !== id),
  );
}

/**
 * A person has read a refused command: drop it (and the commands held behind it on that
 * order). When nothing is left for the order, the till goes back to the server's copy.
 */
export async function dismissProblem(seq: number): Promise<void> {
  const row = await db().queue.get(seq);
  if (!row) return;
  await db().queue.delete(seq);
  if (row.orderId) {
    const left = await db().queue.where('orderId').equals(row.orderId).count();
    if (left === 0) {
      const rec = await db().orders.get(row.orderId);
      if (rec?.server) await db().orders.put({ ...rec, view: rec.server, pending: 0 });
      else await db().orders.delete(row.orderId);
      await db().kv.delete(keys.localPayments(row.outletId, row.orderId));
      await db().kv.delete(keys.invoice(row.outletId, row.orderId));
    } else {
      await recount(row.orderId);
    }
  }
  void queryClient.invalidateQueries({ queryKey: ['pos', row.outletId] });
}

/** Start the background loop: sync when the cloud comes back and while commands wait. */
export function startSync(): () => void {
  const off = connectivity.subscribe(() => {
    if (connectivity.isOnline()) syncSoon(0);
  });
  const timer = setInterval(() => {
    if (connectivity.isOnline()) void syncNow();
  }, 5_000);
  syncSoon(0);
  return () => {
    off();
    clearInterval(timer);
  };
}
